/**
 * AuthProvider — single source of truth for the currently signed-in user.
 *
 * Coexists with the site's public-first UX (no login gate on the Home
 * or Demo experiences). Whenever a page needs the user, it calls
 * `useAuth()` and gets `{ user, loading, refresh, logout, loginWithGoogle }`.
 *
 * Two flows are supported per the Emergent playbook:
 *   1. Google (donors)     — httpOnly `session_token` cookie
 *   2. Email/password (JWT) — Authorization: Bearer <jwt> from localStorage
 *
 * The `/api/auth/me` endpoint handles BOTH (it inspects cookie first,
 * then bearer header). The mount probe only fires when a session can exist:
 * a JWT in localStorage, the `mra_session` marker set on any sign-in (the
 * Google cookie is httpOnly, so this is how we remember it), or an
 * auth-aware route. Anonymous visitors on public pages never trigger a 401.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";
const SESSION_FLAG = "mra_session";
const AUTH_ROUTES = /^\/(dashboard|admin|launch-checklist|login|register|forgot-password|reset-password|recurring-gifts|verify\/|payment\/|campaign\/)/;

const sessionPossible = () =>
  Boolean(localStorage.getItem(TOKEN_KEY) || localStorage.getItem(SESSION_FLAG)) || AUTH_ROUTES.test(window.location.pathname);
const rememberSession = () => localStorage.setItem(SESSION_FLAG, "1");
const forgetSession = () => localStorage.removeItem(SESSION_FLAG);

const AuthContext = createContext(null);

const authClient = axios.create({
  baseURL: API,
  withCredentials: true, // required so the session_token cookie is sent
});

// Attach JWT (email/pass flow) automatically if present in localStorage.
authClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // 3-state loading: null = unknown (checking), true = confirmed, false = anonymous.
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async (force = false) => {
    setLoading(true);
    // If arriving from Emergent OAuth (session_id in fragment), AuthCallback
    // will handle the exchange first; skip this probe to avoid a 401 flash.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (!force && !sessionPossible()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await authClient.get("/auth/me");
      setUser(res.data);
      rememberSession();
    } catch {
      setUser(null);
      forgetSession();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const registerStudent = useCallback(async (body) => {
    const res = await authClient.post("/auth/register", body);
    localStorage.setItem(TOKEN_KEY, res.data.access_token);
    rememberSession();
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const loginWithPassword = useCallback(async (email, password) => {
    const res = await authClient.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, res.data.access_token);
    rememberSession();
    setUser(res.data.user);
    return res.data.user;
  }, []);

  /** Adopt a LoginResponse ({access_token, user}) — used after a password reset. */
  const adoptSession = useCallback((data) => {
    localStorage.setItem(TOKEN_KEY, data.access_token);
    rememberSession();
    setUser(data.user);
    return data.user;
  }, []);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const exchangeGoogleSession = useCallback(async (sessionId) => {
    const res = await authClient.post("/auth/google/session", { session_id: sessionId });
    rememberSession();
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authClient.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem(TOKEN_KEY);
    forgetSession();
    setUser(null);
  }, []);

  const value = {
    user,
    loading,
    refresh: () => checkAuth(true),
    registerStudent,
    loginWithPassword,
    adoptSession,
    loginWithGoogle,
    exchangeGoogleSession,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
